// src/components/SetupWizard/SetupWizardModal.js
//
// Phase 3: Setup Wizard Modal — Container Component
//
// Renders a full-screen MUI Dialog with a 3-step stepper.
// Manages step navigation, resume logic from Firestore flags,
// and orchestrates data flow between wizard steps and the backend.
//
// Props:
//   open          — boolean, controls dialog visibility
//   onClose       — callback to close the modal
//   clientId      — string|null. null = create new client (Step 1 creates it)
//   user          — Firebase auth user object
//   userData      — user document data from Firestore (contains assignedQuestionnaireTemplateId)
//   clients       — array of client documents (from real-time listener)
//   onClientDataChange — callback to refresh parent data after mutations
//   onSetupComplete    — callback when all 3 steps complete and client activates

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

// Step components — placeholder imports, will be replaced as each step is built
import WizardStep1_CompanyIdentity from './WizardStep1_CompanyIdentity';
import WizardStep2_Financials from './WizardStep2_Financials';
import WizardStep3_StrategicIntent from './WizardStep3_StrategicIntent';
import WizardStep4_DocumentUpload from './WizardStep4_DocumentUpload';

const STEP_LABELS = ['Company Identity', 'Financial Context', 'Strategic Intent', 'Add Document (Optional)'];

const SetupWizardModal = ({
  open,
  onClose,
  clientId: initialClientId,
  user,
  userData,
  clients,
  onClientDataChange,
  onSetupComplete
}) => {
  // The clientId may start as null (new client) and get set after Step 1 creates it
  const [activeClientId, setActiveClientId] = useState(initialClientId);
  const [activeStep, setActiveStep] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionError, setCompletionError] = useState(null);
  const [privacyExpanded, setPrivacyExpanded] = useState(false);

  // Sync initialClientId prop when modal opens/reopens
  useEffect(() => {
    if (open) {
      setActiveClientId(initialClientId);
      setIsCompleting(false);
      setCompletionError(null);
    }
  }, [open, initialClientId]);

  // Look up the current client data from the real-time clients array
  const clientData = useMemo(() => {
    if (!activeClientId || !clients) return null;
    return clients.find(c => c.id === activeClientId) || null;
  }, [activeClientId, clients]);

  // ----- Resume Logic -----
  // Determine which step to show based on Firestore flags on the client document.
  // This runs when the modal opens and when clientData updates (real-time listener).
  useEffect(() => {
    if (!open) return;

    // New client (no clientId yet) — always start at Step 0
    if (!activeClientId || !clientData) {
      setActiveStep(0);
      return;
    }

    // Derive effective step from completion flags
    const step1Done = !!clientData.submittedForAdminReview;
    const step2Done = !!clientData.financialsUploaded ||
                      !!clientData.financialsSkipped ||
                      !!clientData.financialRangesProvided;

    // Check Step 3 via status on the client doc or the setupWizardStep field
    // (The real-time ceoQuestionnaireResponses check happens inside WizardStep3)
    const wizardStep = clientData.setupWizardStep;

    let derivedStep = 0;
    if (step1Done) derivedStep = 1;
    if (step1Done && step2Done) derivedStep = 2;

    // Use the higher of derived step and stored wizardStep
    // (handles race conditions where flags haven't propagated yet)
    const effectiveStep = Math.max(derivedStep, wizardStep || 0);

    // Don't go backwards if user is already past the derived step
    setActiveStep(prev => Math.max(prev, effectiveStep));
    }, [open, activeClientId]); // clientData intentionally excluded — resume logic runs on open only, not on every Firestore update

  // ----- Step Navigation -----
  const handleStepComplete = useCallback(async (stepIndex, data = {}) => {
    // If Step 1 returned a new clientId, capture it
    if (stepIndex === 0 && data.clientId) {
      setActiveClientId(data.clientId);
    }

    const targetClientId = data.clientId || activeClientId;

    // Persist the wizard step to Firestore for resume
    if (targetClientId) {
      try {
        const clientRef = doc(db, 'clients', targetClientId);
        await updateDoc(clientRef, {
          setupWizardStep: stepIndex + 1,
          updatedAt: serverTimestamp()
        });
      } catch (err) {
        console.error('[SetupWizard] Failed to persist wizard step:', err);
        // Non-blocking — the step still advances in the UI
      }
    }

    // Advance to next step
    if (stepIndex < STEP_LABELS.length - 1) {
      setActiveStep(stepIndex + 1);
    } else {
      // All steps complete — trigger setup finalization
      handleSetupComplete(targetClientId);
    }

    // Refresh parent data
    if (onClientDataChange) {
      onClientDataChange({ silent: true });
    }
  }, [activeClientId, onClientDataChange]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => Math.max(0, prev - 1));
  }, []);

  // ----- Setup Completion -----
  const handleSetupComplete = async (clientId) => {
    setIsCompleting(true);
    setCompletionError(null);

    // AFTER:
    try {
      // Brief delay for the completion animation, then notify parent and close
      const clientName = clientData?.name || clientData?.companyName || 'New Client';
      setTimeout(() => {
        if (onSetupComplete) {
          onSetupComplete(clientName);
        }
        onClose();
        setIsCompleting(false);
      }, 2000);
    } catch (err) {
      console.error('[SetupWizard] Completion error:', err);
      setCompletionError(err.message || 'Failed to finalize setup. Please try again.');
      setIsCompleting(false);
    }
  };

  // ----- Close Confirmation -----
  const handleClose = () => {
    // If on Step 0 with no client created yet, close freely
    // Otherwise, progress is persisted in Firestore — safe to close
    onClose();
  };

  // ----- Render -----
  const renderStepContent = () => {
    if (isCompleting) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 2
        }}>
          <CircularProgress size={48} />
          <Typography variant="h6">Activating your client...</Typography>
          <Typography variant="body2" color="text.secondary">
            Setting up AI processing pipeline and generating initial insights.
          </Typography>
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <WizardStep1_CompanyIdentity
            clientId={activeClientId}
            clientData={clientData}
            user={user}
            onStepComplete={(data) => handleStepComplete(0, data)}
          />
        );
      case 1:
        return (
          <WizardStep2_Financials
            clientId={activeClientId}
            clientData={clientData}
            user={user}
            onStepComplete={(data) => handleStepComplete(1, data)}
            onBack={handleBack}
          />
        );
       case 2:
        return (
          <WizardStep3_StrategicIntent
            clientId={activeClientId}
            clientData={clientData}
            user={user}
            userData={userData}
            onStepComplete={(data) => handleStepComplete(2, data)}
            onBack={handleBack}
            onClientDataChange={onClientDataChange}
          />
        );
      case 3:
        return (
          <WizardStep4_DocumentUpload
            clientId={activeClientId}
            clientData={clientData}
            user={user}
            onStepComplete={(data) => handleStepComplete(3, data)}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Box>
          <Typography variant="h5" component="span" fontWeight="bold">
            {activeClientId ? 'Client Setup' : 'Set Up New Client'}
          </Typography>
          {clientData?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {clientData.name}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEP_LABELS.map((label, index) => (
            <Step key={label} completed={activeStep > index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content */}
      <DialogContent sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        pt: 1
      }}>
        {completionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCompletionError(null)}>
            {completionError}
          </Alert>
        )}

        {renderStepContent()}

        {/* Collapsible Privacy Section — shown on all steps */}
        <Box sx={{ mt: 'auto', pt: 3 }}>
          <Button
            onClick={() => setPrivacyExpanded(!privacyExpanded)}
            endIcon={privacyExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              fontSize: '0.85rem',
              p: 0,
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            How does SavvaiEdge protect privacy?
          </Button>
          <Collapse in={privacyExpanded}>
            <Box sx={{
              mt: 1,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              borderLeft: '3px solid',
              borderColor: 'primary.main'
            }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>How can you receive personalized insights if your data is anonymized?</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Before any data is sent to AI models, we replace all identifying information — company names, personal names, emails — with generic placeholders like "[the company]". The AI analyzes financial patterns, market trends, and strategic goals without ever knowing your client's identity. We then re-insert your client's name only within your private, secure portal.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Think of it like a doctor reading an X-ray.</strong> They can diagnose the condition and recommend treatment from the medical data alone — they don't need the patient's name. SavvaiEdge works the same way with business intelligence.
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SetupWizardModal;